import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Table, Space, Form, Input, Select, Row, Col, Modal, message, Tooltip, Spin, Typography, Divider, Tag, Dropdown, Card, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { listSurveryApi, delSurveyApi, releaseSurveryApi, surveyMarkAsTesting } from '@services/Wj/Question';
import './index.less';
import Addsurvey from './Addsurvey';
import config from '@config';
import { TABLE_PAGE_SIZE_OPTIONS, computeButtonColLayout, filterFormGridLayout, filterFormItemLayout, filterFormItemStyle } from '@utils/property';
import { convertDateTimeToLocalText, GetQueryString } from '@utils/utils';
import PageHeader from '@components/PageHeader/PageHeader';
import { wjActions } from '../../store/wjSlice';

const { setSurveyFilter: setFilter } = wjActions;
const { Text } = Typography;

const FILTER_FIELD_COUNT = 6;

export default function SurveysList() {
    const { token } = theme.useToken();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    useEffect(() => {
        document.title = '问卷列表-医调研';
    }, []);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const filter = useSelector(state => state.wj.survey.filter);
    const [form] = Form.useForm();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [load, setLoad] = useState(false);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [curItem, setCurItem] = useState(undefined);
    const [iframeSrc, setIframeSrc] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);

    // 全屏与表格滚动三件套
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const onPreviewClick = item => {
        setCurItem(item);
        setIsPreviewModalOpen(true);
    };

    const goto = (type, id, title, row) => {
        switch (type) {
            case 'charts':
                navigate(`/wj/surveys/${id}/charts`, { state: { id: id, name: title } });
                break;
            case 'share':
                if (row.status !== 1) {
                    Modal.confirm({
                        title: '提示',
                        icon: <i className="bi bi-exclamation-circle"></i>,
                        content: '问卷需要发布后再分享，是不是要发布？',
                        okText: '发布',
                        cancelText: '取消',
                        onOk: () => {
                            releaseSurveryApi({ id: row.id, status: 1 }).then(res => {
                                if (res.status_code === 200) {
                                    navigate(`/wj/surveys/${id}/share`, {
                                        state: {
                                            title,
                                            route: 'newList',
                                            id: id,
                                            type: row.surveyTypeId,
                                            surveyStatus: row.status,
                                            action: 'edit',
                                        },
                                    });
                                }
                            });
                        },
                    });
                } else {
                    navigate(`/wj/surveys/${id}/share`, {
                        state: {
                            title,
                            route: 'newList',
                            id: id,
                            type: row.surveyTypeId,
                            surveyStatus: row.status,
                            action: 'edit',
                        },
                    });
                }
                break;
            case 'edit':
                if (row.status === 1) {
                    Modal.confirm({
                        title: '提示',
                        icon: <i className="bi bi-exclamation-circle"></i>,
                        content: '问卷为上架状态不支持修改，需下架问卷后进行编辑',
                        okText: '我知道了',
                        cancelText: '取消',
                    });
                } else {
                    navigate(`/wj/surveys/edit/${id}`, {
                        state: {
                            route: 'newList',
                            id: id,
                            type: row.surveyTypeId,
                            surveyStatus: row.status,
                            action: 'edit',
                            title: row.name,
                        },
                    });
                }
                break;
            default:
                break;
        }
    };

    // 分页
    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const onFormFinish = values => {
        const validValues = {};
        Object.keys(values).forEach(key => {
            const val = values[key];
            if (val !== undefined && val !== null && val !== '') {
                if (Array.isArray(val) && val.length === 0) return;
                validValues[key] = val;
            }
        });
        dispatch(
            setFilter({
                page: 1,
                pageSize: filter.pageSize,
                isTesting: 0,
                ...validValues,
            }),
        );
    };

    const onReset = () => {
        form.resetFields();
        dispatch(
            setFilter({
                page: 1,
                pageSize: filter.pageSize,
                isTesting: 0,
            }),
        );
    };
    const newSurvey = () => {
        setIsModalOpen(true);
    };

    const onDelete = row => {
        Modal.confirm({
            title: '提示',
            content: `确认删除问卷${row?.name}`,
            okText: '确定',
            cancelText: '取消',
            onOk: () => {
                delSurveyApi({ id: row.id }).then(res => {
                    if (res.status_code === 200) {
                        getListData(filter);
                    }
                });
            },
        });
    };

    const onChangeStatus = row => {
        if (row.status === 1) {
            releaseSurveryApi({ id: row.id, status: 2 }).then(res => {
                if (res.status_code === 200) {
                    setLoad(true);
                    getListData(filter).finally(() => setLoad(false));
                }
            });
        } else {
            releaseSurveryApi({ id: row.id, status: 1 }).then(res => {
                if (res.status_code === 200) {
                    setLoad(true);
                    getListData(filter).finally(() => setLoad(false));
                    message.success('问卷已发布');
                }
            });
        }
    };

    const getListData = async (filterParams, projectName) => {
        const { page, pageSize, name, type, status, createdName, business, isTesting } = filterParams;
        const params = {
            page,
            pageSize,
            name: projectName || name,
            isTesting,
        };
        if (type !== undefined) params.surveyTypeId = type;
        if (status !== undefined) params.status = status;
        if (business !== undefined) params.business = business;
        if (createdName !== undefined && createdName !== '') params.createdName = createdName;

        const res = await listSurveryApi(params);
        if (res.status_code === 200) {
            setData(res.data.list);
            setTotal(res.data.totalCount);
        }
    };

    const handleRefresh = () => {
        setLoad(true);
        getListData(filter).finally(() => setLoad(false));
    };

    const markAsTesting = row => {
        Modal.confirm({
            title: '标记为测试问卷',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: '医疗决策人数据统计等数据指标将排除标记为测试的问卷项目，确定标记为测试问卷？',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                surveyMarkAsTesting({
                    id: row.id,
                    isTesting: !!row.isTesting ? 0 : 1,
                }).then(res => {
                    if (res.status_code === 200) {
                        const msg = !!row.isTesting ? '标记为正式问卷成功' : '标记为测试问卷成功';
                        message.success(msg);
                        getListData(filter);
                    }
                });
            },
        });
    };

    const columns = [
        {
            title: '问卷ID',
            dataIndex: 'id',
            key: 'id',
            width: 60,
        },
        {
            title: '问卷名称',
            dataIndex: 'name',
            key: 'name',
            width: 300,
            render: (_, record) => (
                <>
                    <span>{record.name}</span>
                    {!!record.isTesting && (
                        <Tag color="#f50" style={{ marginLeft: 4, padding: '0 4px' }}>
                            TEST
                        </Tag>
                    )}
                </>
            ),
        },
        {
            title: '问卷类型',
            dataIndex: 'surveyTypeId',
            key: 'surveyTypeId',
            width: 68,
            render: surveyTypeId => (
                <Space size="medium">
                    {surveyTypeId === 1 && <span>调研问卷</span>}
                    {surveyTypeId === 2 && <span>投票</span>}
                    {surveyTypeId === 3 && <span>活动报名</span>}
                </Space>
            ),
        },
        {
            title: '所属业务',
            dataIndex: 'business',
            key: 'business',
            width: 68,
            render: business => <Space size="medium">{business === 0 ? <span>调研后台</span> : business === 1 ? <span>健康界</span> : business === 2 ? <span>组织学习</span> : <span>调研后台</span>}</Space>,
        },
        {
            title: '题目数量',
            dataIndex: 'questionsCount',
            key: 'questionsCount',
            width: 68,
        },
        {
            title: '作答人数',
            dataIndex: 'userCount',
            key: 'userCount',
            width: 68,
        },
        {
            title: '问卷状态',
            dataIndex: 'status',
            key: 'status',
            width: 68,
            render: status => (
                <Space size="medium">
                    {status === 0 && <span>未发布</span>}
                    {status === 1 && <Text type="success">已发布</Text>}
                    {status === 2 && <Text type="warning">已下架</Text>}
                </Space>
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            render: (_, row) => (
                <>
                    <span>{row.createdByStaffName || '系统创建'}</span>
                    <br />
                    <small>
                        <Text type="secondary">{convertDateTimeToLocalText(row.createdAt)}</Text>
                    </small>
                </>
            ),
        },
        {
            title: '操作',
            dataIndex: 'state',
            key: 'state',
            fixed: 'right',
            width: 200,
            render: (_, row) => {
                const items = [
                    {
                        label: !!row.isTesting ? '标记为正式问卷' : '标记为测试问卷',
                        key: 'markAsTesting',
                        onClick: () => markAsTesting(row),
                    },
                    {
                        label: '删除问卷',
                        key: 'delete',
                        onClick: () => onDelete(row),
                        danger: true,
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        {(row.status === 0 || row.status === 2) && (
                            <Button type="link" size="small" onClick={() => onChangeStatus(row)}>
                                发布
                            </Button>
                        )}
                        {row.status === 1 && (
                            <Button type="link" size="small" onClick={() => onChangeStatus(row)} danger>
                                下架
                            </Button>
                        )}
                        <Button type="link" size="small" onClick={() => onPreviewClick(row)}>
                            预览
                        </Button>
                        <Button type="link" size="small" onClick={() => goto('share', row.id, row.name, row)}>
                            分享
                        </Button>
                        <Button type="link" size="small" onClick={() => goto('charts', row.id, row.name)}>
                            统计
                        </Button>
                        <Button type="link" size="small" onClick={() => goto('edit', row.id, row.name, row)}>
                            编辑
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots" />
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖 filter 语义即可
    useEffect(() => {
        getListData(filter);
    }, [filter]);

    useEffect(() => {
        const formValues = {};
        Object.keys(filter).forEach(key => {
            if (key === 'page' || key === 'pageSize') return;
            const val = filter[key];
            if (val !== undefined && val !== null && val !== '') {
                formValues[key] = val;
            }
        });
        if (filter.isTesting === 0 || filter.isTesting === 1) {
            formValues.isTesting = filter.isTesting;
        }
        form.setFieldsValue(formValues);
    }, [filter, form]);
    useEffect(() => {
        if (isPreviewModalOpen) {
            setLoading(true);
            setIframeSrc(`${config.SURVEY_DOMAIN}/s/${curItem?.id}/answer?type=1`);
        } else {
            setIframeSrc(undefined);
        }
    }, [isPreviewModalOpen]);

    useEffect(() => {
        if (iframeSrc !== undefined) {
            setLoading(false);
        }
    }, [iframeSrc]);

    // 医项目跳转携带参数
    // biome-ignore lint/correctness/useExhaustiveDependencies: 仅在挂载时执行
    useEffect(() => {
        const projectName = GetQueryString('projectName');
        if (projectName) {
            dispatch(setFilter({ ...filter, name: unescape(projectName), page: 1 }));
        }
    }, []);
    const buttonColLayout = computeButtonColLayout(FILTER_FIELD_COUNT);

    return (
        <div className="page-container list-page-container surveys-list-page">
            <PageHeader title="问卷列表" breadcrumb={[{ label: '医调研' }, { label: '问卷管理' }, { label: '问卷列表' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="surveys-list-filter-panel"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} form={form} name="control-hooks" onFinish={onFormFinish} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="name" label="问卷名称" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="请输入问卷名称" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="business" label="所属业务" style={filterFormItemStyle}>
                                    <Select
                                        placeholder="请选择所属业务"
                                        className="selete_type_wrap"
                                        options={[
                                            { value: 0, label: '调研后台' },
                                            { value: 2, label: '组织学习' },
                                            { value: 1, label: '健康界' },
                                        ]}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="type" label="问卷类型" style={filterFormItemStyle}>
                                    <Select
                                        placeholder="请选择问卷类型"
                                        className="selete_type_wrap"
                                        options={[
                                            { value: 1, label: '调研问卷' },
                                            { value: 2, label: '投票' },
                                            { value: 3, label: '活动报名' },
                                        ]}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item name="status" label="问卷状态" style={filterFormItemStyle}>
                                    <Select
                                        placeholder="请选择问卷状态"
                                        className="selete_type_wrap"
                                        options={[
                                            { value: 0, label: '未发布' },
                                            { value: 1, label: '已发布' },
                                            { value: 2, label: '已下架' },
                                        ]}
                                        allowClear
                                    />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item name="createdName" label="创建人" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="请输入创建人名称" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="正式/测试" name="isTesting" initialValue={0} style={filterFormItemStyle}>
                                    <Select placeholder="选择是否测试问卷" allowClear>
                                        <Select.Option value={0}>正式问卷</Select.Option>
                                        <Select.Option value={1}>测试问卷</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col {...buttonColLayout}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="primary" htmlType="submit" icon={<i className="bi bi-search" aria-hidden="true" />}>
                                            查询
                                        </Button>
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container surveys-list-table-region">
                <div className="main-toolbar surveys-list-table-toolbar surveys-list-toolbar">
                    <div className="main-tools">
                        <Button type="primary" onClick={newSurvey}>
                            新建问卷
                        </Button>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap surveys-list-table-wrap surveys-list">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        columns={columns}
                        dataSource={data}
                        loading={load}
                        size="medium"
                        bordered
                        sticky
                        rowKey={row => row.id}
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            onChange: onChangePage,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                        }}
                    />
                </div>
            </div>

            <Addsurvey isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />

            <Modal open={isPreviewModalOpen} title={curItem?.name || ''} className="share_survey_modal_wrap" centered width={800} onCancel={() => setIsPreviewModalOpen(false)} destroyOnHidden footer={null}>
                {loading ? <Spin /> : <iframe src={iframeSrc} frameBorder="0" title="share" />}
            </Modal>
        </div>
    );
}
